import React, { useState, useEffect } from 'react';
import { Button, Card, Table, Form, Modal } from 'react-bootstrap';
import Topbar from './components/Topbar';
import SuperAdminSidebar from './components/SuperAdminSidebar';
import styles from '../../assets/css/DepartmentManagement.module.css'; 
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify/unstyled';


function Usermanagment() {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    name: '',
    department: '',
    email: '',
    password: '',
  });

  // State for modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changingPasswordUser, setChangingPasswordUser] = useState(null);
  const [passwordData, setPasswordData] = useState({ newPassword: '', confirmPassword: '' });
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [departmentsError, setDepartmentsError] = useState(null);

  useEffect(() => {
    const fetchDepartments = async () => {
      setDepartmentsLoading(true);
      setDepartmentsError(null);
      try {
        const token = localStorage.getItem('token');
        const apiBaseUrl = process.env.REACT_APP_SUPER_ADMIN_API_URL;
        const url = `${apiBaseUrl}/departmentlist.php`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch departments');
        }
        const data = await response.json();
        if (data.success && Array.isArray(data.departments)) {
          setDepartments(data.departments);
        } else {
          setDepartments([]);
          setDepartmentsError(data.message || 'No departments found');
        }
      } catch (error) {
        setDepartmentsError(error.message);
        setDepartments([]);
      } finally {
        setDepartmentsLoading(false);
      }
    };
    fetchDepartments();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = process.env.REACT_APP_SUPER_ADMIN_API_URL;
      const url = `${apiBaseUrl}/userlist.php`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        const formattedUsers = data.map(user => ({
          ...user,
          department: user.department_name
        }));
        setUsers(formattedUsers);
      } else if (
        data.message &&
        (data.message.toLowerCase().includes('no record found') ||
         data.message.toLowerCase().includes('no users found'))
      ) {
        setUsers([]); // No toast, just show empty table
      } else {
        toast.error(data.message || 'Failed to fetch users');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.department || !newUser.email || !newUser.password) {
      return;
    }
    const selectedDepartment = departments.find(
      dept => (dept.name || dept.department_name) === newUser.department
    );
    if (!selectedDepartment) {
      return;
    }
    const department_id = selectedDepartment.id || selectedDepartment.department_id;
    const userToAdd = {
      name: newUser.name,
      department_id: department_id,
      email: newUser.email,
      password: newUser.password,
    };
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = process.env.REACT_APP_SUPER_ADMIN_API_URL;
      const url = `${apiBaseUrl}/adduser.php`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(userToAdd),
      });
      const data = await response.json();
      
      if (data.status === "success") {
        toast.success(data.message);
        setNewUser({ name: '', department: '', email: '', password: '' }); 
        fetchUsers(); 
      } else  {
        toast.error(data.message);
      }
    } catch (error) {
    }
  };

  const handleShowEditModal = (user) => {
    const department = departments.find(d => (d.name || d.department_name) === user.department);
    setEditingUser({ 
      ...user, 
      department_id: department ? (department.id || department.department_id) : '' 
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
  };

  const handleUpdateUser = async () => {
    if (!editingUser.name || !editingUser.department_id || !editingUser.email) {
      toast.warn("Please fill all fields for the user.");
      return;
    }

    const payload = {
      id: editingUser.id,
      name: editingUser.name,
      department_id: parseInt(editingUser.department_id, 10),
      email: editingUser.email,
    };

    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = process.env.REACT_APP_SUPER_ADMIN_API_URL;
      const url = `${apiBaseUrl}/edituser.php`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      
      if (data.status === "success") {
        toast.success(data.message || "User updated successfully!");
        handleCloseEditModal();
        fetchUsers();
      } else {
        toast.error(data.message || "Failed to update user.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error("Update user error:", error);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingUser({ ...editingUser, [name]: value });
  };

  const handleShowDeleteModal = (user) => {
    setDeletingUser(user);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingUser(null);
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const apiBaseUrl = process.env.REACT_APP_SUPER_ADMIN_API_URL;
      const url = `${apiBaseUrl}/deleteuser.php`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_id: deletingUser.id }),
      });

      const data = await response.json();

      if (data.status === "success") {
        toast.success(data.message || "User deleted successfully!");
        fetchUsers();
      } else {
        toast.error(data.message || "Failed to delete user.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error("Delete user error:", error);
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleShowChangePasswordModal = (user) => {
    setChangingPasswordUser(user);
    setShowChangePasswordModal(true);
  };

  const handleCloseChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setChangingPasswordUser(null);
    setPasswordData({ newPassword: '', confirmPassword: '' });
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }
    if (!passwordData.newPassword) {
      toast.error("New password cannot be empty.");
      return;
    }
    
    const payload = {
        user_id: changingPasswordUser.id,
        password: passwordData.newPassword,
    };

    try {
        const token = localStorage.getItem('token');
        const apiBaseUrl = process.env.REACT_APP_SUPER_ADMIN_API_URL;
        const url = `${apiBaseUrl}/userchangepassword.php`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (data.status === "success") {
            toast.success(data.message || "Password changed successfully!");
            handleCloseChangePasswordModal();
        } else {
            toast.error(data.message || "Failed to change password.");
        }
    } catch (error) {
        toast.error("An error occurred while changing password.");
        console.error("Change password error:", error);
    }
  };

  return (
    <>
    <ToastContainer/>
      <Topbar />
      <div className="d-flex" style={{ minHeight: 'calc(100vh - 56px)' }}>
        <SuperAdminSidebar />
        <div className="flex-grow-1 p-3 p-md-4 bg-light">
          <header className="mb-4">
            <h1 className="h2">User Management</h1>
          </header>

          {/* Create New User Section */}
          <div className="card col-md-6 mb-4">
            <div className={styles.cardHeader}>
              <span className={styles.cardHeaderIcon}>+</span> Create New User
            </div>
            <div className="card-body">
              <form onSubmit={handleCreateUser}>
                <div className="mb-3">
                  <label htmlFor="userName" className="form-label">
                    Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="userName"
                    name="name"
                    placeholder="Enter name"
                    value={newUser.name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="department" className="form-label">
                    Department
                  </label>
                  <Form.Select
                    id="department"
                    name="department"
                    value={newUser.department}
                    onChange={handleInputChange}
                    disabled={departmentsLoading || !!departmentsError}
                  >
                    <option value="">{departmentsLoading ? 'Loading...' : departmentsError ? 'Error loading departments' : 'Select Department'}</option>
                    {departments && departments.map && departments.map(dept => (
                      <option key={dept.id || dept.department_id || dept.name} value={dept.name || dept.department_name}>{dept.name || dept.department_name}</option>
                    ))}
                  </Form.Select>
                  {departmentsError && (
                    <div className="text-danger small mt-1">{departmentsError}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label htmlFor="userEmail" className="form-label">
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="userEmail"
                    name="email"
                    placeholder="Enter email"
                    value={newUser.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="userPassword">
                    Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="userPassword"
                    name="password"
                    placeholder="Enter password"
                    value={newUser.password}
                    onChange={handleInputChange}
                  />
                </div>
                <button type="submit" className={`btn btn-primary ${styles.createButton}`}>
                  Add User
                </button>
              </form>
            </div>
          </div>

          {/* User List Section */}
          <div className="card">
            <Card.Header as="h5" style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #1976d2' }}>
              <span style={{ color: '#1976d2' }}>User List</span>
            </Card.Header>
            <div className="card-body">
              <Table striped bordered hover responsive="sm">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Department</th>
                    <th>Email</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center">No record found</td>
                    </tr>
                  ) : (
                    users.map((user, index) => (
                      <tr key={user.id}>
                        <td>{index + 1}</td>
                        <td>{user.name}</td>
                        <td>{user.department}</td>
                        <td>{user.email}</td>
                        <td className="d-flex gap-2">
                          <Button variant="warning" size="sm" onClick={() => handleShowEditModal(user)}>Edit</Button>
                          <Button variant="danger" size="sm" onClick={() => handleShowDeleteModal(user)}>Delete</Button>
                          <Button variant="info" size="sm" onClick={() => handleShowChangePasswordModal(user)}>Change Password</Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3" controlId="formEditUserName">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder="Enter name"
                  value={editingUser.name}
                  onChange={handleEditInputChange}
                  autoFocus
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="formEditUserDepartment">
                <Form.Label>Department</Form.Label>
                <Form.Select name="department_id" value={editingUser.department_id || ''} onChange={handleEditInputChange} disabled={departmentsLoading || !!departmentsError}>
                  <option value="">Select Department</option>
                  {departmentsLoading ? (
                    <option disabled>Loading...</option>
                  ) : departmentsError ? (
                    <option disabled>Error loading departments</option>
                  ) : (
                    departments.map(dept => (
                      <option key={dept.id || dept.department_id} value={dept.id || dept.department_id}>{dept.name || dept.department_name}</option>
                    ))
                  )}
                </Form.Select>
                {departmentsError && (
                  <div className="text-danger small mt-1">{departmentsError}</div>
                )}
              </Form.Group>
              <Form.Group className="mb-3" controlId="formEditUserEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Enter email"
                  value={editingUser.email}
                  onChange={handleEditInputChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>Close</Button>
            <Button variant="primary" onClick={handleUpdateUser}>Save Changes</Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete the user <strong>"{deletingUser.name}" </strong>?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDeleteModal}>Cancel</Button>
            <Button variant="danger" onClick={handleDeleteUser}>Delete</Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Change Password Modal */}
      {changingPasswordUser && (
        <Modal show={showChangePasswordModal} onHide={handleCloseChangePasswordModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Change Password for {changingPasswordUser.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3" controlId="formNewPassword">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  name="newPassword"
                  placeholder="Enter new password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordInputChange}
                  autoFocus
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="formConfirmPassword">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm new password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordInputChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseChangePasswordModal}>Close</Button>
            <Button variant="primary" onClick={handleChangePassword}>Save Changes</Button>
          </Modal.Footer>
        </Modal>
      )}

    </>
  );
}

export default Usermanagment;
